import { h, Component, Fragment } from 'preact';

import localUtil from '../../lib/util';
import { postSponsorEvents } from '../../lib/sponsorEvents';

const VIEW_VISIBLE_RATIO = 0.5;

/**
 * Sponsor bar shown over the bottom edge of the hero media (autoplay video or
 * image) on the frontpage and the article view page. One tile per
 * article.sponsors[idx]: the logo, a one-line caption and an outbound link,
 * both taken from the index-matched article.sponsorsMeta[idx] ({ url, name }).
 * Same parallel-array shape as convention.sponsors / convention.sponsorsMeta.
 *
 * `inline` renders the same tiles as a normal strip (no absolute positioning,
 * solid dark background) for placing under media that draws its own bottom
 * controls, such as the streaming player on the article view page.
 *
 * Tracking (only when `articleId` is set): one `view` per logo is posted the
 * first time at least half of the bar is on screen, once per mount; a `click`
 * is posted when a logo link is tapped (the link still opens normally).
 * Sizing lives in custom.css (.article-sponsor-*): 60px logos on desktop,
 * 30px on phones.
 *
 * Renders null when there are no sponsors, so callers can always mount it.
 */
class ArticleSponsorOverlay extends Component {
    viewsPosted = false;

    componentDidMount() {
        this.observeBar();
    }

    componentWillUnmount() {
        this.disconnect();
    }

    observeBar = () => {
        const { articleId } = this.props;
        if (!(articleId > 0) || !this.barEl || typeof IntersectionObserver === 'undefined') {
            return;
        }
        this.observer = new IntersectionObserver(entries => {
            const visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= VIEW_VISIBLE_RATIO);
            if (visible) {
                this.postViews();
            }
        }, { threshold: [VIEW_VISIBLE_RATIO] });
        this.observer.observe(this.barEl);
    }

    disconnect = () => {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }

    postViews = () => {
        if (this.viewsPosted) {
            return;
        }
        this.viewsPosted = true;
        this.disconnect();
        const { articleId, surface = 'article', sponsors = [] } = this.props;
        const events = sponsors
            .filter(sponsor => sponsor && sponsor.md5)
            .map(sponsor => ({ md5: sponsor.md5, event: 'view', surface }));
        postSponsorEvents({ articleId, events });
    }

    onSponsorClick = (md5) => {
        const { articleId, surface = 'article' } = this.props;
        if (!(articleId > 0) || !md5) {
            return;
        }
        postSponsorEvents({ articleId, events: [{ md5, event: 'click', surface }] });
    }

    render() {
        const { sponsors = [], sponsorsMeta = [], inline = false, surface = 'article' } = this.props;
        if (!Array.isArray(sponsors) || sponsors.length === 0) {
            return null;
        }
        // Surface class lets custom.css size logos per surface (frontpage logos are 10px shorter).
        const surfaceClass = `article-sponsor-surface-${surface}`;
        const barClass = inline
            ? `article-sponsor-overlay article-sponsor-inline ${surfaceClass} w-100 d-flex flex-row flex-wrap justify-content-center align-items-end`
            : `article-sponsor-overlay ${surfaceClass} position-absolute bottom-0 start-0 w-100 d-flex flex-row flex-wrap justify-content-center align-items-end`;
        const barStyle = inline
            ? 'background: rgba(0,0,0,0.65);'
            : 'background: linear-gradient(to top, rgba(0,0,0,0.65), rgba(0,0,0,0)); pointer-events: none;';
        return (
            <div class={barClass} style={barStyle} ref={el => { this.barEl = el; }}>
                {sponsors.map((sponsor, idx) => {
                    const meta = (Array.isArray(sponsorsMeta) && sponsorsMeta[idx]) || {};
                    const name = meta.name || '';
                    const src = localUtil.displayImg(sponsor, {}, '400x');
                    const inner = <>
                        <img src={src} alt={name} loading='lazy' class='img-fluid article-sponsor-logo' />
                        {name && <small class='article-sponsor-name d-block text-white text-center text-truncate'>{name}</small>}
                    </>;
                    const key = sponsor.md5 || sponsor.uuidv4 || idx;
                    if (meta.url) {
                        return (
                            <a key={key} href={meta.url} target='_blank' rel='noopener noreferrer'
                                class='article-sponsor-item d-flex flex-column align-items-center text-decoration-none'
                                style='pointer-events: auto;'
                                onClick={() => this.onSponsorClick(sponsor.md5)}>
                                {inner}
                            </a>
                        );
                    }
                    return (
                        <div key={key} class='article-sponsor-item d-flex flex-column align-items-center'>
                            {inner}
                        </div>
                    );
                })}
            </div>
        );
    }
}

export default ArticleSponsorOverlay;
